import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostListener,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { GoogleMap, MapMarker } from '@angular/google-maps';

import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, take, takeUntil, tap } from 'rxjs/operators';

import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';
import { DialogConfig, DialogRef } from '../../../dynamic-dialog/public_api';
import { ShIconComponent } from '../../../elements/components/icon/icon.component';
import { ShNotificationComponent } from '../../../elements/components/notification/notification.component';
import { LoadingBehaviorSubject } from '../../../helpers/loading-behavior-subject.helper';
import { ShGoogleMapsService } from '../../services/google-maps/google-maps.service';

@Component({
    selector: 'sh-google-maps-dialog',
    styleUrls: ['./google-maps-dialog.component.scss'],
    templateUrl: './google-maps-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [ShNotificationComponent, ShIconComponent, GoogleMap, MapMarker, AsyncPipe, TranslateModule]
})
export class ShGoogleMapsDialogComponent extends BaseContainer implements OnInit {
    private readonly BELGIUM_ZOOM: number = 7.5;
    private readonly DETAIL_ZOOM: number = 14;
    private readonly ADDRESS_INPUT_DEBOUNCE_TIME: number = 2000;
    private readonly INPUT_MIN_CHARS: number = 4;

    private readonly defaultZoom$: Observable<number>;

    public belgiumCoordinate: google.maps.LatLngLiteral = { lat: 50.4818831, lng: 4.4357424 };
    public markerPosition$: BehaviorSubject<google.maps.LatLngLiteral | null> =
        new BehaviorSubject<google.maps.LatLngLiteral | null>(null);

    public locationIsLoading$: LoadingBehaviorSubject = new LoadingBehaviorSubject();

    public readonly googleMapsOptions$: Observable<google.maps.MapOptions>;

    public markerOptions = { draggable: true };

    private readonly searchTerm$: Subject<string> = new Subject<string>();
    private readonly autocompleteLoading$: LoadingBehaviorSubject = new LoadingBehaviorSubject();
    private readonly autocompleteSuggestions$: Subject<google.maps.places.QueryAutocompletePrediction[]> = new Subject<
        google.maps.places.QueryAutocompletePrediction[]
    >();

    private searchInput: HTMLInputElement;
    private autocompleteDropdown: HTMLDivElement;
    private autocompleteList: HTMLElement;

    private sessionToken: google.maps.places.AutocompleteSessionToken;
    private autocompleteService: google.maps.places.AutocompleteService;
    private placesService: google.maps.places.PlacesService;

    public invalidCoordinatesError$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    googleMap?: GoogleMap;
    @ViewChild('googleMap') set _googleMap(googleMap: GoogleMap) {
        if (googleMap && googleMap instanceof GoogleMap) {
            this.googleMap = googleMap;
            this.googleMapIsReady$.next(true);
        }
    }

    public hasGeolocationNavigatorSupport: boolean = 'geolocation' in navigator;

    public mapIsInitialized = false;

    apiLoaded$: Observable<boolean>;
    googleMapIsReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        private readonly dialogConfig: DialogConfig<string>,
        private readonly dialogRef: DialogRef<string>,
        private readonly cd: ChangeDetectorRef,
        private readonly googleMapsSandBox: ShGoogleMapsService
    ) {
        super();

        this.defaultZoom$ = this.googleMapsSandBox.getClientZoomLevel();

        this.googleMapsOptions$ = this.defaultZoom$.pipe(
            take(1),
            map(zoomLevel => ({
                zoom: zoomLevel,
                mapTypeId: 'roadmap',
                clickableIcons: false,
                restriction: {
                    // global restriction (no gray areas)
                    latLngBounds: {
                        east: 180,
                        north: 85,
                        south: -85,
                        west: -180
                    },
                    strictBounds: true
                }
            }))
        );

        this.apiLoaded$ = this.googleMapsSandBox.publicLoadApi();
    }

    @HostListener('document:keydown.enter', ['$event'])
    handleEnter(event: KeyboardEvent) {
        if (!this.markerPosition$.value) {
            return;
        }
        this.applyLocation();
    }

    ngOnInit(): void {
        this.setupGoogleMapsAfterLoad();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();

        this.locationIsLoading$.complete();
        this.autocompleteLoading$.complete();
        this.searchTerm$.complete();
        this.autocompleteSuggestions$.complete();
    }

    public deleteMarker(): void {
        this.markerPosition$.next(null);
    }

    public jumpToMarker(): void {
        if (this.markerPosition$.value) {
            this.setCenter(this.markerPosition$.value);
        }
    }

    public useMyLocation(): void {
        this.locationIsLoading$.startLoading();

        navigator.geolocation.getCurrentPosition(
            successfullLocation => this.handleMyLocation(successfullLocation),
            () => {
                this.locationIsLoading$.stopLoading();
            }
        );
    }

    public applyLocation(): void {
        const coordinate = this.markerPosition$.value;

        if (coordinate) {
            const coordinateAsString = `${coordinate?.lat}, ${coordinate?.lng}`;
            this.dialogRef.close(coordinateAsString);
        }
    }

    private setupGoogleMapsAfterLoad(): void {
        combineLatest([this.apiLoaded$.pipe(take(1)), this.googleMapIsReady$])
            .pipe(
                filter(([apiLoaded, googleMapIsReady]) => apiLoaded && googleMapIsReady),
                take(1),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.addCustomControls();
                this.setInitialState();
            });
    }

    private setInitialState(): void {
        const mockMap: HTMLDivElement = document.createElement('div');
        this.placesService = new google.maps.places.PlacesService(mockMap);
        this.autocompleteService = new google.maps.places.AutocompleteService();

        const coordinate = this.dialogConfig.data;
        if (coordinate?.trim()) {
            if (!coordinate.includes(',')) {
                this.invalidCoordinatesError$.next(true);
                this.setLocationAndZoomBasedOnSettings();
                return;
            }

            this.setLocationAndMarkerForCoordinate(coordinate, this.DETAIL_ZOOM);
            return;
        }

        this.setLocationAndZoomBasedOnSettings();
    }

    private setLocationAndZoomBasedOnSettings(): void {
        const clientCoordinate$: Observable<google.maps.LatLngLiteral | null> =
            this.googleMapsSandBox.getClientLocation();

        combineLatest([clientCoordinate$, this.defaultZoom$])
            .pipe(take(1))
            .subscribe(([clientCoordinate, zoomLevel]) => {
                if (clientCoordinate) {
                    this.setCenter(clientCoordinate);
                    this.setZoom(zoomLevel);
                } else {
                    this.setCenter(this.belgiumCoordinate);
                    this.setZoom(this.BELGIUM_ZOOM);
                }
            });
    }

    private setLocationAndMarkerForCoordinate(coordinate: string | undefined, zoomLevel: number): void {
        if (!coordinate) {
            return;
        }
        const [lat, lng] = coordinate.split(',');
        const coords: google.maps.LatLngLiteral = {
            lat: Number(lat),
            lng: Number(lng)
        };

        if (Object.values(coords).some(value => Number.isNaN(value))) {
            this.invalidCoordinatesError$.next(true);
            this.setLocationAndZoomBasedOnSettings();
            return;
        }

        this.updateMarker(coords);
        this.setCenter(coords);
        this.setZoom(zoomLevel);
        this.cd.detectChanges();
    }

    // #region Controls
    private addCustomControls(): void {
        this.addSearchboxControl();
        this.addNavigateToLocationControl();
        this.addJumpToMarkerControl();
        this.addDeleteMarkerControl();
    }

    private addDeleteMarkerControl(): void {
        const deleteMarker: HTMLElement | null = document.getElementById('delete');
        if (deleteMarker) {
            this.googleMap!.controls[google.maps.ControlPosition.RIGHT_TOP].push(deleteMarker);
        }
    }

    private addJumpToMarkerControl(): void {
        const jumpToMarker: HTMLElement | null = document.getElementById('location');
        if (jumpToMarker) {
            this.googleMap!.controls[google.maps.ControlPosition.RIGHT_TOP].push(jumpToMarker);
        }
    }

    private addNavigateToLocationControl(): void {
        if (this.hasGeolocationNavigatorSupport) {
            const panToLocationButton: HTMLButtonElement = document.createElement('button');
            panToLocationButton.className = 'gm-style sh-gm-button';
            panToLocationButton.onclick = () => this.useMyLocation();
            panToLocationButton.textContent = this.translateService.instant(
                'modules.general.form.location.move-to-current-location'
            );
            this.locationIsLoading$.pipe(takeUntil(this.destroy$)).subscribe(isLoading => {
                panToLocationButton.disabled = isLoading;
            });

            this.googleMap!.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(panToLocationButton);
        }
    }

    private addSearchboxControl(): void {
        const searchInput: HTMLDivElement = this.buildSearchboxControl();

        this.initInputListener();
        this.initAutocompleteListener();

        this.googleMap!.controls[google.maps.ControlPosition.TOP_CENTER].push(searchInput);
    }

    private buildSearchboxControl(): HTMLDivElement {
        const formselect: HTMLDivElement = document.createElement('div');
        formselect.style.fontSize = '12px';

        this.searchInput = document.createElement('input');
        this.searchInput.className = 'sh-input-field sh-form-multiselect__select';
        this.searchInput.type = 'text';
        this.searchInput.placeholder = this.translateService.instant('ui.search');
        this.searchInput.style.width = '300px';
        this.searchInput.style.margin = '10px';

        this.autocompleteDropdown = document.createElement('div');
        this.autocompleteDropdown.className = 'sh-droppanel -sh-is-full-width -sh-is-open';
        this.autocompleteDropdown.style.width = '300px';
        this.autocompleteDropdown.style.margin = '10px';
        this.autocompleteDropdown.style.display = 'none';
        this.autocompleteList = document.createElement('ul');
        this.autocompleteList.className = 'sh-droppanel__list';

        this.autocompleteDropdown.append(this.autocompleteList);

        formselect.append(this.searchInput);
        formselect.append(this.autocompleteDropdown);

        return formselect;
    }

    private initInputListener() {
        this.searchInput.addEventListener('input', () => this.searchTerm$.next(this.searchInput.value));

        this.searchTerm$
            .pipe(
                distinctUntilChanged(),
                tap(() => this.autocompleteLoading$.startLoading()),
                debounceTime(this.ADDRESS_INPUT_DEBOUNCE_TIME),
                tap(searchTerm => this.findPlaces(searchTerm)),
                takeUntil(this.destroy$)
            )
            .subscribe();

        this.searchTerm$
            .pipe(map(input => input.length > 0 && this.autocompleteDropdown.style.display === 'none'))
            .subscribe(() => this.autocompleteSuggestions$.next([]));
    }

    private findPlaces(searchTerm: string): void {
        if (searchTerm.length < this.INPUT_MIN_CHARS) {
            this.autocompleteLoading$.stopLoading();
            return this.autocompleteSuggestions$.next([]);
        }

        if (!this.sessionToken) {
            this.sessionToken = new google.maps.places.AutocompleteSessionToken();
        }

        this.autocompleteService.getPlacePredictions(
            {
                input: searchTerm,
                sessionToken: this.sessionToken
            },
            this.setSuggestions.bind(this)
        );
    }

    private setSuggestions(
        result: google.maps.places.QueryAutocompletePrediction[] | null,
        status: google.maps.places.PlacesServiceStatusString
    ) {
        this.autocompleteLoading$.stopLoading();
        if (status != google.maps.places.PlacesServiceStatus.OK || !result) {
            this.autocompleteSuggestions$.next([]);
            return;
        }

        this.autocompleteSuggestions$.next(result);
    }

    private initAutocompleteListener() {
        combineLatest([this.autocompleteSuggestions$, this.autocompleteLoading$])
            .pipe(
                debounceTime(50),
                tap(([suggestions, loading]) => this.buildAutocompleteList(suggestions, loading)),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    private buildAutocompleteList(results: google.maps.places.QueryAutocompletePrediction[], isLoading: boolean): void {
        let results_html: HTMLElement[];

        this.autocompleteList.innerText = '';

        if (isLoading) {
            const loadingElement: HTMLElement = document.createElement('li');
            loadingElement.className = 'sh-droppanel__list__item -sh-disabled';
            const label: HTMLElement = document.createElement('label');
            label.className = 'sh-droppanel__list-action';
            label.innerText = this.translateService.instant('ui.search-loading');
            loadingElement.append(label);

            results_html = [loadingElement];
        } else if (results.length > 0) {
            results_html = results.map((prediction: google.maps.places.QueryAutocompletePrediction) => {
                const element: HTMLElement = document.createElement('li');
                element.className = 'sh-droppanel__list__item';

                const label: HTMLElement = document.createElement('label');
                label.className = 'sh-droppanel__list-action';
                label.innerText = prediction.description;

                element.onclick = () => {
                    this.handleSuggestionClick(prediction);
                };
                element.append(label);
                return element;
            });
        } else {
            const noResultsElement: HTMLElement = document.createElement('li');
            noResultsElement.className = 'sh-droppanel__list__item -sh-disabled';
            const label: HTMLElement = document.createElement('label');
            label.className = 'sh-droppanel__list-action';
            label.innerText = this.translateService.instant('ui.no-results');
            noResultsElement.append(label);

            results_html = [noResultsElement];
        }

        this.autocompleteList.append(...results_html);

        this.autocompleteList.style.display = 'block';
        this.autocompleteDropdown.style.display = 'block';
    }

    private handleSuggestionClick(prediction: google.maps.places.QueryAutocompletePrediction) {
        const request = {
            placeId: prediction.place_id ?? '',
            fields: ['name', 'geometry']
        };

        this.placesService.getDetails(request, (place, status) => {
            if (status == google.maps.places.PlacesServiceStatus.OK && place) {
                if (!place.geometry?.location || !place.name) {
                    console.error('Returned place contains no geometry');
                    return;
                }
                this.autocompleteDropdown.style.display = 'none';
                this.searchInput.innerText = place.name;

                this.setCenter(place.geometry.location);
                this.setZoom(this.DETAIL_ZOOM);
                this.updateMarker({
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng()
                });
                this.cd.detectChanges();
            }
        });
    }
    // #endregion

    private handleMyLocation(successfullLocation: GeolocationPosition): void {
        this.setCenter({
            lat: successfullLocation.coords.latitude,
            lng: successfullLocation.coords.longitude
        });
        this.setZoom(this.DETAIL_ZOOM);
        this.locationIsLoading$.stopLoading();
    }

    public addOrUpdateMarker(event: google.maps.MapMouseEvent): void {
        this.updateMarker({
            lat: event?.latLng?.lat() ?? 0,
            lng: event?.latLng?.lng() ?? 0
        });
    }

    private updateMarker(coordinate: google.maps.LatLngLiteral): void {
        this.markerPosition$.next(coordinate);
    }

    // #region Maps Functions
    private setCenter(latlng: google.maps.LatLng | google.maps.LatLngLiteral): void {
        this.googleMap!.googleMap!.setCenter(latlng);
    }

    private setZoom(zoom: number): void {
        this.googleMap!.googleMap!.setZoom(zoom);
    }
    // #endregion

    close(): void {
        this.dialogRef.close();
    }

    public get invalidMsg(): string {
        return `${this.translateService.translate('form.location.invalid-location')} ${this.translateService.translate(
            'modules.general.form.location.click-to-add-location'
        )}`;
    }
}
